import { SxProps, Theme } from "@mui/material";
import { brandAlpha, eyebrowPill, hoverLift } from "../../../theme/mixins";

export const pageWrapperStyles: SxProps<Theme> = {
    maxWidth: 900,
    mx: 'auto',
    p: 2,
    pt: 0,
};

export const pageHeaderContainerStyles: SxProps<Theme> = {
    textAlign: 'center',
    mb: 4,
};

export const eyebrowStyles: SxProps<Theme> = eyebrowPill;

export const pageHeaderTitleStyles: SxProps<Theme> = {
    fontWeight: 700,
    letterSpacing: '-0.015em',
};

export const pageHeaderSubtitleStyles: SxProps<Theme> = {
    color: 'text.secondary',
    maxWidth: 600,
    mx: 'auto',
};

// The tab strip stays pinned while the page itself scrolls.
export const tabsStyles: SxProps<Theme> = {
    mb: 3,
    width: '100%',
    position: 'sticky',
    top: 72,
    zIndex: 10,
    backgroundColor: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 999,
    border: '1px solid rgba(16,24,40,0.06)',
    '& .MuiTabs-indicator': {
        display: 'none',
    },
    '& .MuiTab-root': {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 999,
        minHeight: 44,
    },
    '& .Mui-selected': {
        backgroundColor: brandAlpha('primary', 0.10),
    },
};

export const sectionBoxStyles: SxProps<Theme> = { mb: 5 };

export const sectionTitleStyles: SxProps<Theme> = {
    mb: 2,
    textTransform: 'uppercase',
    letterSpacing: '0.09em',
    textAlign: 'center',
    fontSize: '0.95rem',
    fontWeight: 700,
};

export const serviceCardStyles: SxProps<Theme> = {
    mb: 3,
    p: 2.5,
    ...hoverLift,
};

export const serviceNameStyles: SxProps<Theme> = {
    mb: 1,
    fontWeight: 600,
};

export const serviceListItemStyles: SxProps<Theme> = {
    px: 0,
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    pl: 1.5,
};

export const servicePriceStyles = (theme: Theme): SxProps<Theme> => ({
    whiteSpace: 'nowrap',
    fontWeight: 700,
    color: theme.palette.primary.main,
});

export const dividerStyles: SxProps<Theme> = { ml: 9 };

/**
 * Edit, under the centred header rather than beside it: the header is a marketing banner
 * the admin also reads as a customer, and hanging a button off it would pull it off
 * centre for everyone.
 */
export const headerActionsStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    gap: 1.5,
    mb: 3,
};

/**
 * Save / Cancel while editing, pinned below the app bar for as long as edit mode lasts.
 *
 * A price list runs to dozens of rows, so the admin is almost always at the bottom of it
 * when they finish a change -- and a Save that has scrolled off the top means going back
 * up to commit and back down to carry on. Pinning it also keeps the reason Save is
 * unavailable on screen next to the button, instead of that being a fact you can only
 * discover by making the trip.
 *
 * Same offset and frosted treatment as the read view's tab strip, which sticks to the
 * same place; the two are never on screen together.
 */
export const editingActionsStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1.5,
    mb: 3,
    py: 1.5,
    px: 2,
    position: 'sticky',
    top: 72,
    zIndex: 10,
    backgroundColor: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 999,
    border: '1px solid rgba(16,24,40,0.06)',
};

/** Why Save is unavailable, beside it rather than only down at the offending field. */
export const blockedSaveHintStyles: SxProps<Theme> = {
    color: 'error.main',
    fontWeight: 600,
};

export const alertStyles: SxProps<Theme> = { mb: 3 };

export const loadingBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 8,
};

export const emptyStateBoxStyles: SxProps<Theme> = {
    textAlign: 'center',
    py: 6,
};

export const emptyStateSubtitleStyles: SxProps<Theme> = {
    color: 'text.secondary',
    maxWidth: 480,
    mx: 'auto',
};
